/**
 * Program 12.1 UX4 — Bid Control Room (unified bid preparation workspace).
 */
import React from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Drawer,
  Form,
  Grid,
  List,
  Modal,
  Row,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { CommercialLifecycleTimeline } from '@/components/commercial-lifecycle/CommercialLifecycleTimeline';
import { EpcAddendaPanel } from '@/components/epc/commercial-ux/EpcAddendaPanel';
import { EpcBidBondPanel } from '@/components/epc/commercial-ux/EpcBidBondPanel';
import { EpcBidBriefPanel } from '@/components/epc/commercial-ux/EpcBidBriefPanel';
import { EpcBidDecisionCockpit } from '@/components/epc/commercial-ux/EpcBidDecisionCockpit';
import { EpcBidStrategyPanel } from '@/components/epc/commercial-ux/EpcBidStrategyPanel';
import { EpcClarificationsPanel } from '@/components/epc/commercial-ux/EpcClarificationsPanel';
import { EpcProposalWorkspace } from '@/components/epc/commercial-ux/EpcProposalWorkspace';
import { EpcEstimateWorkspaceShell } from '@/components/epc/estimation/EpcEstimateWorkspaceShell';
import { EpcBcrEstimateStatusCard } from '@/components/epc/estimation/EpcBcrEstimateStatusCard';
import { EpcSubmissionControlPanel } from '@/components/epc/commercial-ux/EpcSubmissionControlPanel';
import { buildBidWorkspaceViewModel } from '@/components/epc/commercial-ux/bidWorkspaceModel';
import {
  buildProposalReadiness,
  buildEstimateSummaryReadiness,
  normalizeSections,
  seedTechnicalSections,
} from '@/components/epc/commercial-ux/estimateProposalSubmissionReadiness';
import { pursuitResolvePath } from '@/components/epc/commercial-ux/resolvePursuitOpportunityId';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

function workstreamPct(
  proposal: Record<string, unknown> | null,
  estimateCount: number,
  approvedRevisionCount: number,
  latestRevisionStatus?: string | null
): Record<string, number> {
  const est = buildEstimateSummaryReadiness({
    estimateCount,
    approvedRevisionCount,
    latestRevisionStatus,
  });
  const prop = proposal ? buildProposalReadiness(proposal) : null;
  const tech = proposal
    ? normalizeSections(proposal.technical_sections_json ?? proposal.technicalSectionsJson)
    : [];
  const techRows = tech.length ? tech : seedTechnicalSections().map((s) => ({ key: s.key, complete: false }));
  const techPct = techRows.length
    ? Math.round((techRows.filter((r) => r.complete).length / techRows.length) * 100)
    : 0;
  const estPct = Math.round(
    (est.checks.filter((c) => c.state === 'READY').length / Math.max(est.checks.length, 1)) * 100
  );
  const commPct =
    prop?.overall === 'READY' ? 100 : prop?.overall === 'IN_PROGRESS' ? 50 : 0;
  return { estimate: estPct, technical: techPct, commercial: commPct, documents: 0 };
}

const EpcBidControlRoomPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const screens = useBreakpoint();
  const [briefOpen, setBriefOpen] = React.useState(false);
  const [bidContextOpen, setBidContextOpen] = React.useState(false);
  const [deptCreateKind, setDeptCreateKind] = React.useState<'bid' | 'submission' | null>(null);
  const [deptForm] = Form.useForm();
  const [activeTab, setActiveTab] = React.useState(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return t || 'bid-decision';
  });
  const estimateFocus = activeTab === 'estimate';

  React.useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    if (t && t !== activeTab) setActiveTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wsQ = useQuery({
    queryKey: ['epc-tender-workspace', id],
    queryFn: () => epcCommercialService.getTenderWorkspace(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });

  const opportunityId = wsQ.data
    ? String(wsQ.data.tender.opportunity_id || wsQ.data.tender.opportunityId || '') || null
    : null;

  const estListQ = useQuery({
    queryKey: ['epc-est-list', id],
    queryFn: () => epcEstimationService.list(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
  const estRows = Array.isArray(estListQ.data) ? estListQ.data : [];
  const estimateCount = estRows.length;
  const approvedRevisionCount = estRows.filter((e: any) =>
    /approved/i.test(String(e.current_revision_status || e.revision_status || e.status || ''))
  ).length;
  const latestRevisionStatus = String(
    estRows[0]?.current_revision_status ||
      estRows[0]?.revision_status ||
      estRows[0]?.status ||
      ''
  ) || null;

  const createBidM = useMutation({
    mutationFn: (departmentId: string) =>
      epcCommercialService.createBidDecision({ tenderId: id!, departmentId }),
    onSuccess: () => {
      message.success('Bid decision created');
      setDeptCreateKind(null);
      deptForm.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', id] });
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  const createProposalM = useMutation({
    mutationFn: () =>
      epcCommercialService.createProposal({
        tenderId: id!,
        currency: String(wsQ.data?.tender.currency || 'AED'),
      }),
    onSuccess: () => {
      message.success('Proposal created');
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', id] });
      qc.invalidateQueries({ queryKey: ['commercial-lifecycle-context', 'epc_tender', id] });
    },
  });

  const createSubmissionM = useMutation({
    mutationFn: (departmentId: string) => {
      const prop = (wsQ.data?.proposals || [])[0];
      return epcCommercialService.createSubmission({
        tenderId: id!,
        proposalId: prop ? String(prop.id) : undefined,
        departmentId,
      });
    },
    onSuccess: () => {
      message.success('Submission created');
      setDeptCreateKind(null);
      deptForm.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', id] });
      qc.invalidateQueries({ queryKey: ['commercial-lifecycle-context', 'epc_tender', id] });
      setActiveTab('submission');
    },
    onError: (e: Error) => message.error(e.message || 'Create submission failed'),
  });

  const patchProposalM = useMutation({
    mutationFn: ({ pid, payload }: { pid: string; payload: Record<string, unknown> }) =>
      epcCommercialService.patchProposal(pid, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['epc-tender-workspace', id] }),
  });

  if (!id) return null;

  if (wsQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (wsQ.isError || !wsQ.data) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message={(wsQ.error as Error)?.message || 'Failed to load bid workspace'} />
      </div>
    );
  }

  const { tender, bidDecisions, proposals, submissions, clarifications, addenda, risks } = wsQ.data;
  const primaryBd = bidDecisions[0] || null;
  const primaryProposal = proposals[0] || null;
  const primarySubmission = submissions[0] || null;
  const wsPct = workstreamPct(
    primaryProposal,
    estimateCount,
    approvedRevisionCount,
    latestRevisionStatus
  );

  const vm = buildBidWorkspaceViewModel({
    tender,
    bidDecisions,
    proposals,
    submissions,
    clarifications,
    addenda,
    estimateCount,
    approvedRevisionCount,
    latestRevisionStatus,
  });

  const toggleTechSection = (key: string, complete: boolean) => {
    if (!primaryProposal) return;
    const pid = String(primaryProposal.id);
    const tech = normalizeSections(
      primaryProposal.technical_sections_json ?? primaryProposal.technicalSectionsJson
    );
    const seeded = tech.length ? tech : seedTechnicalSections().map((s) => ({ key: s.key, complete: false }));
    const next = seeded.map((s) => ({
      key: s.key,
      status: s.key === key ? (complete ? 'complete' : 'missing') : s.complete ? 'complete' : 'missing',
    }));
    patchProposalM.mutate({ pid, payload: { technicalSectionsJson: next } });
  };

  const intelligenceRail = (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <EpcBidBriefPanel vm={vm} proposal={primaryProposal} />
      <EpcBidBondPanel tenderId={id} tender={tender} />
      {risks.length > 0 && (
        <Card size="small" title="Risk snapshot">
          <List
            size="small"
            dataSource={risks.slice(0, 5)}
            renderItem={(r) => (
              <List.Item>
                <Tag>{String(r.severity || '—')}</Tag>
                <Text>{String(r.title || r.description || '—')}</Text>
              </List.Item>
            )}
          />
        </Card>
      )}
    </Space>
  );

  const mainTabs = [
    {
      key: 'bid-decision',
      label: 'Bid Decision',
      children: primaryBd ? (
        <EpcBidDecisionCockpit row={primaryBd} tender={tender} />
      ) : (
        <Card>
          <Alert type="info" showIcon message="No bid decision yet" style={{ marginBottom: 12 }} />
          <Button type="primary" onClick={() => setDeptCreateKind('bid')}>
            Create bid decision
          </Button>
        </Card>
      ),
    },
    {
      key: 'strategy',
      label: 'Strategy',
      disabled: vm.isNoBidApproved,
      children: (
        <EpcBidStrategyPanel tenderId={id} tender={tender} workstreamPct={wsPct} />
      ),
    },
    {
      key: 'estimate',
      label: 'Estimate',
      disabled: vm.isNoBidApproved,
      children: null,
    },
    {
      key: 'proposal',
      label: 'Proposal',
      disabled: vm.isNoBidApproved,
      children: primaryProposal ? (
        <EpcProposalWorkspace
          tenderId={id}
          tender={tender}
          proposal={primaryProposal}
          onToggleTechSection={toggleTechSection}
          techPending={patchProposalM.isPending}
          hasSubmission={!!primarySubmission}
          onGoEstimate={() => setActiveTab('estimate')}
          onGoSubmission={() => setActiveTab('submission')}
          onCreateSubmission={() => setDeptCreateKind('submission')}
          createSubmissionPending={createSubmissionM.isPending}
        />
      ) : (
        <Card>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="No proposal yet"
            description="Create a proposal after estimation, then certify the Approved estimate basis and move to Submission."
          />
          <Button type="primary" loading={createProposalM.isPending} onClick={() => createProposalM.mutate()}>
            Create proposal
          </Button>
        </Card>
      ),
    },
    {
      key: 'clarifications',
      label: 'Clarifications',
      disabled: vm.isNoBidApproved,
      children: <EpcClarificationsPanel tenderId={id} rows={clarifications} />,
    },
    {
      key: 'addenda',
      label: 'Addenda',
      disabled: vm.isNoBidApproved,
      children: <EpcAddendaPanel tenderId={id} rows={addenda} />,
    },
    {
      key: 'submission',
      label: 'Submission',
      disabled: vm.isNoBidApproved,
      children: (
        <EpcSubmissionControlPanel
          tenderId={id}
          tender={tender}
          submission={primarySubmission}
          proposals={proposals}
          bidDecisions={bidDecisions}
          clarifications={clarifications}
          addenda={addenda}
        />
      ),
    },
  ];

  return (
    <div
      style={{
        padding: estimateFocus ? 0 : 16,
      }}
    >
      {estimateFocus ? (
        <EpcEstimateWorkspaceShell
          focusMode
          immersiveBoq
          tenderId={id!}
          tenderTitle={String((tender as any)?.title || (tender as any)?.tender_number || '')}
          bcrNav={{
            onBackTenders: () => navigate('/epc/commercial/tenders'),
            onClassicWorkspace: () => setActiveTab('bid-decision'),
            onOpenProposal: () => setActiveTab('proposal'),
            onOpenPursuit: () => navigate(pursuitResolvePath('epc_tender', id!)),
            tenderTitle: vm.tenderTitle,
            onBidContext: () => setBidContextOpen(true),
          }}
        />
      ) : (
        <>
      <Space wrap style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/epc/commercial/tenders')}>
          Tenders
        </Button>
        <Button onClick={() => navigate(`/epc/commercial/tenders/${id}`)}>Classic workspace</Button>
        <Button
          onClick={() => navigate(pursuitResolvePath('epc_tender', id))}
        >
          Open pursuit
        </Button>
        {!screens.lg ? (
          <Button onClick={() => setBriefOpen(true)}>Intelligence</Button>
        ) : null}
      </Space>

          <Card style={{ marginBottom: 16 }}>
            <Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>
              {vm.tenderTitle}
            </Title>
            <Text type="secondary">
              {vm.clientName} · Bid Control Room
            </Text>
            <Row gutter={[16, 8]} style={{ marginTop: 12 }}>
              <Col xs={12} md={6}>
                <Text type="secondary">Submission due</Text>
                <div>{vm.submissionDueLabel}</div>
              </Col>
              <Col xs={12} md={6}>
                <Text type="secondary">Submission readiness</Text>
                <div>{vm.overallSubmissionReadiness}%</div>
              </Col>
              <Col xs={24} md={12}>
                <Space wrap>
                  {vm.stages.map((s) => (
                    <Tag key={s.key}>{s.label}: {s.status}</Tag>
                  ))}
                </Space>
              </Col>
            </Row>
            {vm.attention.length > 0 && (
              <List
                size="small"
                style={{ marginTop: 12 }}
                header={<Text strong>Attention</Text>}
                dataSource={vm.attention.slice(0, 5)}
                renderItem={(a) => (
                  <List.Item>
                    <Tag color={a.severity === 'critical' ? 'red' : a.severity === 'warning' ? 'orange' : 'blue'}>
                      {a.severity}
                    </Tag>
                    <Text>{a.title}</Text>
                  </List.Item>
                )}
              />
            )}
          </Card>

          <CommercialLifecycleTimeline entityType="epc_tender" entityId={id} />

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={16}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={mainTabs}
          />
        </Col>
        {screens.lg ? <Col xs={24} lg={8}>{intelligenceRail}</Col> : null}
      </Row>
        </>
      )}

      <Drawer title="Intelligence" open={briefOpen} onClose={() => setBriefOpen(false)} width={360}>
        {intelligenceRail}
      </Drawer>

      <Drawer title="Bid context" open={bidContextOpen} onClose={() => setBidContextOpen(false)} width={420}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="info"
            showIcon
            message="EPC Contracting Estimation (EST3)"
            description="Cost Model READY ≠ Commercial Pricing READY ≠ Approval APPROVED ≠ Submission Basis CERTIFIED."
          />
          <EpcBcrEstimateStatusCard tenderId={id!} proposal={primaryProposal} />
        </Space>
      </Drawer>

      <Modal
        title={deptCreateKind === 'submission' ? 'Create submission' : 'Create bid decision'}
        open={!!deptCreateKind}
        onCancel={() => {
          setDeptCreateKind(null);
          deptForm.resetFields();
        }}
        onOk={() => deptForm.submit()}
        confirmLoading={createBidM.isPending || createSubmissionM.isPending}
        destroyOnClose
      >
        <Form
          form={deptForm}
          layout="vertical"
          onFinish={(v) => {
            const departmentId = String(v.department_id);
            if (deptCreateKind === 'submission') createSubmissionM.mutate(departmentId);
            else createBidM.mutate(departmentId);
          }}
        >
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department is required' }]}
          >
            <DepartmentSelect />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EpcBidControlRoomPage;
